import { defineStore } from "pinia";
import { UserProfile } from "./entity";
import { UserProfileList } from "./list.gen";
import type { UserProfileStore, UserProfileStoreState } from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const userProfileList = new UserProfileList();
export const useUserProfileStore = defineStore("userProfileStore", {
  state: (): UserProfileStoreState => ({
    activeID: null,
    cache: {},
    list: [] as UserProfile[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-UserProfile", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.UserProfile"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(userProfile: UserProfile): Promise<void> {
      // Set active
      await userProfile
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = userProfile.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(userProfile: UserProfile): void {
      const id = userProfile.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(userProfile);

        // Set active
        if (this.active === null) {
          this.activeID = userProfile.id;
        }
      } else {
        this.reinsert(userProfile);
      }
      this.updateKey += 1;
      this.emitStoreUpdateEvent();
    },
    convertList(): void {
      this.list = (this.list as UserProfile[]).map((x: any) => {
        const userProfile = new UserProfile({});
        userProfile.set(x as JSONData);
        return userProfile;
      });
    },
    async delete(userProfile: UserProfile): Promise<void> {
      await userProfile
        .delete()
        .then(() => {
          this.remove(userProfile);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const userProfile = (this.list as UserProfile[]).find(
        (x: UserProfile) => x.id === id,
      );
      if (userProfile !== undefined) {
        await this.delete(userProfile);
      } else {
        throw new Error("Could not find UserProfile with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as UserProfile[]).some((x: UserProfile) => x.id === id);
    },
    get(id: string): UserProfile | null {
      return (
        (this.list as UserProfile[]).find((x: UserProfile) => x.id === id) ||
        null
      );
    },
    reinsert(userProfile: UserProfile): void {
      const index = (this.list as UserProfile[]).findIndex(
        (x: UserProfile) => x.id === userProfile.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          userProfile,
          ...this.list.slice(index + 1),
        ];
        this.updateKey += 1;
        // this.emitStoreUpdateEvent();
      } else {
        this.add(userProfile);
      }
    },
    remove({ id }: UserProfile): void {
      const index = (this.list as UserProfile[]).findIndex(
        (x: UserProfile) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
      this.emitStoreUpdateEvent();
    },
    async retrieve(id: string, force = false): Promise<UserProfile> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const userProfile = this.get(id);
        if (userProfile !== null) {
          return userProfile;
        }
      }

      // Retrieve userProfile
      const userProfile = new UserProfile({});

      // Get userProfile
      await userProfile.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate userProfile
        await this.activate(entity);
      });

      return userProfile;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: UserProfile[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as UserProfile[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await userProfileList.getAll().then(async (list) => {
        list.forEach((userProfile) => {
          this.reinsert(userProfile);
        });

        // Activate userProfile
        if (list.length > 0) {
          if (userProfileList.active !== null) {
            this.activeID = userProfileList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("user_profile_id", this.activeID);
      }

      // Emit store update event
      // this.emitStoreUpdateEvent();

      return {
        active: this.activeID,
        items: this.list as UserProfile[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: UserProfile[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as UserProfile),
        };
      }

      // Retrieve list
      const items = await userProfileList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((userProfile) => {
            this.reinsert(userProfile);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: UserProfile) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Emit store update event
      // this.emitStoreUpdateEvent();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: UserProfile[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as UserProfile),
        };
      }

      // Retrieve list
      const items = await userProfileList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((userProfile) => {
            this.reinsert(userProfile);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: UserProfile) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Emit store update event
      // this.emitStoreUpdateEvent();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: UserProfile[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as UserProfile),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await userProfileList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((userProfile) => {
            this.reinsert(userProfile);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: UserProfile) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Emit store update event
      // this.emitStoreUpdateEvent();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(userProfile: UserProfile): Promise<string> {
      // Update userProfile
      await userProfile.update();

      // Reinsert userProfile
      this.reinsert(userProfile);

      // Activate userProfile
      if (this.active === null) {
        await this.activate(userProfile);
      }

      this.emitStoreUpdateEvent();

      return userProfile.id;
    },
  },
  getters: {
    active(): UserProfile | null {
      if (this.activeID === null) {
        return null;
      }

      const userProfile = (this.list as UserProfile[]).find(
        (x: UserProfile) => x.id === this.activeID,
      );
      if (userProfile === undefined) {
        return null;
      }

      return userProfile;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createUserProfileStore(): UserProfileStore {
  const store = useUserProfileStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as UserProfileStore; // TODO: Fix this
}
